/**
 * Tiny allowlist HTML sanitizer for user-submitted blog bodies.
 *
 * The Blogs API returns `description` as HTML (the legacy CRA app rendered it
 * with `dangerouslySetInnerHTML` after running it through DOMPurify). V2 has no
 * DOMPurify dependency, so this implements a small, conservative allowlist using
 * the browser DOMParser: it keeps a fixed set of formatting/structural tags,
 * drops everything else (unwrapping unknown elements to keep their text), strips
 * all `on*` handlers + `javascript:`/`data:` URLs, and forces external links to
 * open safely.
 *
 * It deliberately mirrors the legacy hardening (no script/ins/iframe/object/
 * embed/form/style/link/meta/base, no inline event handlers, no ad attributes)
 * so a malicious blog body can't inject scripts or foreign ad units.
 */

/** Element tags that survive sanitization. */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'span', 'div', 'blockquote', 'pre', 'code',
  'b', 'strong', 'i', 'em', 'u', 's', 'sub', 'sup', 'mark', 'small',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

/** Per-tag attribute allowlist; everything else is stripped. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
};

/**
 * Disallowed tags whose *text content* must be dropped too (not unwrapped) — a
 * `<script>` body is executable, a `<style>` body is CSS, etc. Everything else
 * disallowed is unwrapped so its visible text survives.
 */
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript',
  'template', 'head', 'title', 'link', 'meta', 'base', 'form',
]);

/** URL schemes considered safe for href/src. */
const SAFE_URL = /^(https?:|mailto:|tel:|\/|#|\.\/|\.\.\/)/i;

function isSafeUrl(value: string): boolean {
  const v = value.trim();
  if (v === '') return false;
  // Block anything that isn't an explicitly safe scheme or relative path.
  return SAFE_URL.test(v);
}

function cleanElement(el: Element, doc: Document): void {
  const tag = el.tagName.toLowerCase();

  // Dangerous content tags: remove the element AND its contents outright.
  if (DROP_WITH_CONTENT.has(tag)) {
    el.parentNode?.removeChild(el);
    return;
  }

  // Recurse first so children are sanitized before we decide on the parent.
  for (const child of Array.from(el.children)) cleanElement(child, doc);

  if (!ALLOWED_TAGS.has(tag)) {
    // Unwrap: replace the element with its (already-sanitized) child nodes so
    // text content survives but the disallowed tag is removed.
    const parent = el.parentNode;
    if (parent) {
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    }
    return;
  }

  const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (!allowed.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }
    if ((name === 'href' || name === 'src') && !isSafeUrl(attr.value)) {
      el.removeAttribute(attr.name);
    }
  }

  if (tag === 'a' && el.getAttribute('href')) {
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer nofollow');
  }
  if (tag === 'img') {
    el.setAttribute('loading', 'lazy');
    el.setAttribute('decoding', 'async');
  }
}

/**
 * Sanitize a blog HTML string to an allowlisted subset. Returns an empty string
 * for empty input or when run without a DOM (SSR / tests without jsdom).
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  if (!body) return '';
  for (const child of Array.from(body.children)) cleanElement(child, doc);
  return body.innerHTML;
}
